import {
  enumUnit,
  InventoryTransactionType,
  LotStatus,
  Unit,
} from '@rematter/frontend-lib-api-client';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export type LotAllocationRecord = {
  id: string;
  movementId: string;
  itemId: string;
  itemName: string;
  quantity: number;
  quantityUnit: Unit;
  occurredAt: string;
  createdAt: string;
  originTransactionType: InventoryTransactionType;
  originTransactionItemId: string;
};

export type LotQuantitySummaryRecord = {
  itemId: string;
  itemName: string;
  quantity: number;
  quantityUnit: Unit;
  inQuantity: number;
  outQuantity: number;
};

export type InventoryLotDetail = {
  id: string;
  lotNumber: string;
  facilityId: string;
  facilityName: string;
  zoneId?: string;
  zoneName?: string;
  zoneCode?: string;
  status: LotStatus;
  description?: string;
  allocations: LotAllocationRecord[];
  allocationsTotalCount: number;
  quantitySummary: LotQuantitySummaryRecord[];
  createdAt: string;
  updatedAt: string;
};

export const inventoryLotQueryAtom = createQueryAtomFactory(
  'inventory.inventoryLot',
  (params: {
    lotId: string;
    allocationsLimit?: number;
    allocationsOffset?: number;
  }) => ({
    inventoryLot: {
      __args: {
        id: params.lotId,
      },
      id: true,
      lotNumber: true,
      facilityId: true,
      facility: {
        id: true,
        facilityName: true,
      },
      zoneId: true,
      zone: {
        id: true,
        name: true,
        zoneCode: true,
      },
      status: true,
      description: true,
      allocations: {
        __args: {
          pagination: {
            limit: params.allocationsLimit ?? 50,
            offset: params.allocationsOffset ?? 0,
          },
        },
        entries: {
          id: true,
          movementId: true,
          movement: {
            id: true,
            itemId: true,
            item: {
              id: true,
              name: true,
            },
            occurredAt: true,
            originTransactionType: true,
            originTransactionItemId: true,
          },
          quantity: {
            value: true,
            unit: true,
          },
          timestamps: {
            createdAt: true,
          },
        },
        page: {
          total: true,
        },
      },
      quantitySummary: {
        itemId: true,
        item: {
          id: true,
          name: true,
        },
        quantity: {
          value: true,
          unit: true,
        },
        inQuantity: {
          value: true,
          unit: true,
        },
        outQuantity: {
          value: true,
          unit: true,
        },
      },
      timestamps: {
        createdAt: true,
        updatedAt: true,
      },
    },
  }),
  {
    select: (data): InventoryLotDetail | null => {
      if (!data || !data.inventoryLot) {
        return null;
      }

      const lot = data.inventoryLot;

      return {
        id: lot.id,
        lotNumber: lot.lotNumber,
        facilityId: lot.facilityId,
        facilityName: lot.facility?.facilityName || '',
        zoneId:
          lot.zoneId && typeof lot.zoneId === 'string' ? lot.zoneId : undefined,
        zoneName:
          lot.zone && typeof lot.zone === 'object' && 'name' in lot.zone
            ? (lot.zone.name as string)
            : undefined,
        zoneCode:
          lot.zone && typeof lot.zone === 'object' && 'zoneCode' in lot.zone
            ? (lot.zone.zoneCode as string)
            : undefined,
        status: lot.status,
        description: lot.description || undefined,
        allocations: (lot.allocations?.entries || []).map((alloc) => ({
          id: alloc.id,
          movementId: alloc.movementId,
          itemId: alloc.movement?.itemId || '',
          itemName: alloc.movement?.item?.name || '',
          quantity: Number(alloc.quantity?.value) || 0,
          quantityUnit: alloc.quantity?.unit || enumUnit.Pounds,
          occurredAt: alloc.movement?.occurredAt || '',
          createdAt: alloc.timestamps?.createdAt || '',
          originTransactionType: alloc.movement?.originTransactionType,
          originTransactionItemId:
            alloc.movement?.originTransactionItemId || '',
        })),
        allocationsTotalCount: lot.allocations?.page?.total || 0,
        quantitySummary: (lot.quantitySummary || []).map((summary) => ({
          itemId: summary.itemId,
          itemName: summary.item?.name || '',
          quantity: Number(summary.quantity?.value) || 0,
          quantityUnit: summary.quantity?.unit || enumUnit.Pounds,
          inQuantity: Number(summary.inQuantity?.value) || 0,
          outQuantity: Number(summary.outQuantity?.value) || 0,
        })),
        createdAt: lot.timestamps?.createdAt || '',
        updatedAt: lot.timestamps?.updatedAt || '',
      };
    },
  },
);
